import { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";

/**
 * Custom hook to manage active tab state via URL search parameters.
 * @param {string} paramName - The name of the query parameter (e.g., "tab").
 * @param {string} defaultTab - The default tab value if the parameter is missing.
 * @returns {Object} - An object containing activeTab and a function to set it.
 */
export const useQueryTab = (paramName = "tab", defaultTab = "events") => {
    const location = useLocation();
    const history = useHistory();

    const getTabFromURL = () => {
        const params = new URLSearchParams(location.search);
        return params.get(paramName) || defaultTab;
    };

    const [activeTab, setActiveTab] = useState(getTabFromURL());

    // Listen for browser navigation changes (forward/back)
    useEffect(() => {
        const tabFromUrl = getTabFromURL();
        if (tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
    }, [location.search, paramName]);

    const changeTab = (newTab) => {
        if (newTab === activeTab) return;

        setActiveTab(newTab);
        const params = new URLSearchParams(location.search);
        params.set(paramName, newTab);

        history.push({
            pathname: location.pathname,
            search: params.toString(),
        });
    };

    return [activeTab, changeTab];
};

export default useQueryTab;
